import {
  otherTeam,
  type AstrolabeId,
  type KnockedOutBall,
  type MatchPhase,
  type RoundEndReason,
  type ShotInput,
  type ShotScore,
  type TeamId,
  type TurnEndReason,
} from "@scb/protocol";
import { simulateShot, type SimBall, type SimResult, type TableSpec } from "@scb/sim";
import { ballsForTeam, type RulesConfig } from "./config";
import {
  buildOccupancy,
  cloneOccupancy,
  emptyOccupancy,
  grandSlamPlate,
  type Occupancy,
} from "./occupancy";
import { firstShooterTeam } from "./seating";
import {
  emptyPlateAccum,
  knockedOutBalls,
  scoreShot,
  type PlateAccum,
  type ShotCapture,
} from "./scoring";

export interface TeamState {
  score: number;
  /** 손에 남은 공 */
  ballsRemaining: number;
  /** 팀 내 다음 슈터 인덱스 (플레이어 수로 나눈 나머지를 쓴다) */
  shotCursor: number;
  plateAccum: PlateAccum;
  consecutiveTimeouts: number;
}

export interface MatchState {
  phase: MatchPhase;
  /** 1부터 시작. 연장은 roundCount + 1 */
  round: number;
  /** 현재 라운드/연장이 끝나는 시각 (epoch ms). phase가 round/overtime일 때만 유효 */
  phaseEndsAtMs: number;
  turnTeam: TeamId;
  teams: [TeamState, TeamState];
  balls: SimBall[];
  nextBallId: number;
  /** 라운드 시드 — 리플레이 재현용 */
  seed: number;
  /** 라운드 내 샷 순번 */
  shotId: number;
  occupancy: Occupancy;
  /** Final Sequence 선택을 기다리는 팀 */
  finalSequenceTeam: TeamId | null;
  /** 이번 라운드에 Final Sequence를 이미 물어봤는지. 라운드당 한 번만 묻는다. */
  finalSequenceDone: boolean;
  winner: TeamId | null;
  endReason: "score" | "overtime" | "forfeit" | null;
}

export interface CreateMatchArgs {
  table: TableSpec;
  config: RulesConfig;
  /** 팀당 인원 (공 개수 스케일링에 쓰임) */
  teamSize: number;
  seed: number;
}

function createTeamState(config: RulesConfig, teamSize: number): TeamState {
  return {
    score: 0,
    ballsRemaining: ballsForTeam(config, teamSize),
    shotCursor: 0,
    plateAccum: emptyPlateAccum(),
    consecutiveTimeouts: 0,
  };
}

export function createMatchState(args: CreateMatchArgs): MatchState {
  const { table, config, teamSize, seed } = args;
  return {
    phase: "lobby",
    round: 0,
    phaseEndsAtMs: 0,
    turnTeam: 0,
    teams: [createTeamState(config, teamSize), createTeamState(config, teamSize)],
    balls: [],
    nextBallId: 1,
    seed,
    shotId: 0,
    occupancy: emptyOccupancy(table),
    finalSequenceTeam: null,
    finalSequenceDone: false,
    winner: null,
    endReason: null,
  };
}

/**
 * 라운드를 시작한다. 테이블을 비우고 양 팀 공을 채운다.
 *
 * 자리(좌/우)는 `launchSideFor(round, team)`으로 라운드에서 유도되므로 여기서
 * 따로 저장하지 않는다 — 서버와 클라이언트가 같은 함수를 써서 어긋날 일이 없다.
 * 선공도 자리에서 유도한다 (오른쪽 자리부터). 자리가 라운드마다 교대되므로
 * 선공도 자동으로 번갈아 간다.
 */
export function startRound(
  state: MatchState,
  args: {
    table: TableSpec;
    config: RulesConfig;
    round: number;
    nowMs: number;
    /** 팀당 인원 — 공 개수 스케일링에 쓰인다 */
    teamSize: number;
  },
): void {
  const { table, config, round, nowMs, teamSize } = args;
  const isOvertime = round > config.roundCount;

  state.phase = isOvertime ? "overtime" : "round";
  state.round = round;
  state.phaseEndsAtMs =
    nowMs + (isOvertime ? config.overtimeDurationMs : config.roundDurationMs);
  state.balls = [];
  state.occupancy = emptyOccupancy(table);
  state.shotId = 0;
  state.finalSequenceTeam = null;
  state.finalSequenceDone = false;
  // 라운드마다 시드를 갈아서 같은 샷이 매 라운드 똑같이 반복되지 않게 한다.
  state.seed = mixSeed(state.seed, round * 7919);

  for (const team of state.teams) {
    team.ballsRemaining = ballsForTeam(config, teamSize);
    team.plateAccum = emptyPlateAccum();
    team.consecutiveTimeouts = 0;
  }

  state.turnTeam = firstShooterTeam(round);
}

export function mixSeed(seed: number, salt: number): number {
  return (Math.imul(seed ^ salt, 0x9e3779b1) ^ (salt << 5)) | 0;
}

export interface ApplyShotArgs {
  state: MatchState;
  table: TableSpec;
  config: RulesConfig;
  team: TeamId;
  shot: ShotInput;
  /** 리플레이 프레임 샘플링 (Hz) */
  sampleHz?: number;
}

export interface ShotOutcome {
  sim: SimResult;
  /** 이 샷에 사용된 시드 — 클라이언트가 리플레이를 재현할 때 쓴다 */
  seed: number;
  /** 슈팅한 팀이 이 샷으로 얻은 점수 */
  shooterScore: ShotScore;
  /** 밀려 들어간 상대 공으로 상대가 얻은 점수 (없으면 total 0) */
  opponentScore: ShotScore;
  /** 이 샷으로 칸에서 빠져나간 공들. penalty는 knockoutPenalty가 꺼져 있으면 0. */
  knockouts: KnockedOutBall[];
  totals: [number, number];
  turnEnded: boolean;
  turnEndReason: TurnEndReason | null;
  /** Final Sequence 선택 대기가 걸린 팀 */
  finalSequenceOffered: TeamId | null;
  /** 이 샷으로 라운드를 더 진행할 수 없게 됐는지 */
  roundOver: boolean;
  /** roundOver가 true일 때 그 이유. false면 null. */
  roundOverReason: RoundEndReason | null;
  /** 이 샷으로 완성된 만관 (판을 통째로 채운 팀). 없으면 null. */
  grandSlam: { astrolabe: AstrolabeId; team: TeamId } | null;
}

/**
 * 샷 하나를 처리한다. `state`를 제자리에서 변경한다 (서버가 소유한 상태이므로 의도적).
 * 시뮬레이션과 점수 계산 자체는 순수 함수라 리플레이·검증이 가능하다.
 */
export function applyShot(args: ApplyShotArgs): ShotOutcome {
  const { state, table, config, team, shot } = args;

  const before = cloneOccupancy(state.occupancy);
  // 샷 이전에 안착해 있던 공들 — 시뮬레이션 뒤 "빠져나간 공" 판정에 쓴다.
  // sim이 state.balls를 교체하므로 반드시 그 전에 떠 둬야 한다.
  const capturedBefore = new Map<number, AstrolabeId>();
  for (const b of state.balls) {
    if (b.state === "captured" && b.astrolabe !== null) capturedBefore.set(b.id, b.astrolabe);
  }
  const seed = mixSeed(state.seed, state.shotId);
  const ballId = state.nextBallId++;

  const sim = simulateShot({
    table,
    balls: state.balls,
    shot,
    ballId,
    team,
    seed,
    sampleHz: args.sampleHz,
  });

  state.balls = sim.balls;
  state.occupancy = buildOccupancy(table, state.balls);
  state.shotId++;

  const byId = new Map<number, SimBall>();
  for (const b of state.balls) byId.set(b.id, b);

  // 이번 샷에서 포획된 공들을 착지 순서대로 모은다.
  // 내 샷이 상대 공을 밀어 넣으면 그 점수는 상대 것이다 (규칙 §6).
  const captures: ShotCapture[] = [];
  for (const ev of sim.events) {
    if (ev.type !== "capture") continue;
    // 재안착(맞아서 빠졌다가 다시 들어간 공)은 점수를 주지 않는다.
    if (!ev.first) continue;
    const ball = byId.get(ev.ballId);
    if (!ball) continue;
    captures.push({
      ballId: ev.ballId,
      team: ball.team,
      astrolabe: ev.astrolabe,
      slot: ev.slot,
    });
  }

  const opp = otherTeam(team);
  const shooterState = state.teams[team];

  shooterState.ballsRemaining = Math.max(0, shooterState.ballsRemaining - 1);
  shooterState.consecutiveTimeouts = 0;

  const scored = scoreShot({
    config,
    before,
    captures,
    plateAccum: [state.teams[0].plateAccum, state.teams[1].plateAccum],
  });
  state.teams[0].plateAccum = scored.plateAccum[0];
  state.teams[1].plateAccum = scored.plateAccum[1];
  state.teams[0].score += scored.scores[0].total;
  state.teams[1].score += scored.scores[1].total;

  // ── 빠져나간 공 (선택 규칙: knockoutPenalty) ──────────
  // 득점 가산 뒤, 턴·승부 판정 전에 차감한다 — totals와 연장 판정이
  // 차감까지 반영된 점수를 봐야 한다. 점수와 판 누적은 0 밑으로 내리지 않는다.
  const knockouts = knockedOutBalls(config, capturedBefore, state.balls);
  for (const k of knockouts) {
    if (k.penalty <= 0) continue;
    const owner = state.teams[k.team];
    owner.score = Math.max(0, owner.score - k.penalty);
    owner.plateAccum[k.astrolabe] = Math.max(0, owner.plateAccum[k.astrolabe] - k.penalty);
  }

  const shooterResult = { score: scored.scores[team] };
  const opponentResult = { score: scored.scores[opp] };

  // ── 턴 판정 ────────────────────────────────────────────
  const shooterScored = shooterResult.score.balls.length > 0;
  const opponentScored = opponentResult.score.balls.length > 0;

  let turnEnded: boolean;
  let turnEndReason: TurnEndReason | null = null;

  if (opponentScored) {
    // 상대 공을 득점 위치로 밀어 넣었다 → 내 점수는 챙기고 턴 종료, 컨트롤은 상대에게
    turnEnded = true;
    turnEndReason = "opponent_scored";
  } else if (shooterScored) {
    turnEnded = false;
  } else {
    turnEnded = true;
    const shotBall = byId.get(ballId);
    turnEndReason = shotBall?.state === "grouped" ? "grouping" : "miss";
  }

  if (shooterState.ballsRemaining === 0) {
    turnEnded = true;
    turnEndReason = turnEndReason ?? "out_of_balls";
  }

  // "rotate"는 매 샷마다 다음 팀원에게 넘기고, "hot-hand"는 턴이 끝날 때만 넘긴다.
  if (config.teamShotOrder === "rotate" || turnEnded) {
    shooterState.shotCursor++;
  }

  if (turnEnded) {
    /**
     * 상대가 공을 다 썼으면 턴을 넘기지 않는다.
     *
     * 넘기면 **쏠 수 없는 팀의 차례**가 되어 라운드가 그 자리에 멈춘다. 그 팀이
     * 뭘 보내도 `out_of_balls`로 거부되고, 양 팀이 0이 아니라서 라운드 종료
     * 조건도 만족하지 않기 때문이다. 실제로 1v1 스모크 테스트가 여기서 교착됐다.
     *
     * 규칙상으로도 이게 맞다 — Final Sequence에서 "계속 진행"을 고르면 공이
     * 남은 쪽이 남은 공을 **전부** 쓴다 (docs/01-game-rules.md §6).
     */
    state.turnTeam = state.teams[opp].ballsRemaining > 0 ? opp : team;
  }

  // ── 만관 (즉시 라운드 종료) ────────────────────────────
  // **샷 이후 점유 현황**으로 본다. 밀려난 공까지 반영된 최종 배치라
  // "넣었지만 같은 샷에서 다른 공이 빠져 만관이 깨진 경우"를 올바르게 걸러낸다.
  //
  // Final Sequence보다 먼저 판정한다 — 라운드가 이미 끝났는데 "남은 공을
  // 환산할래?"를 물으면 답할 라운드가 없다.
  const slam = config.endRoundOnGrandSlam ? grandSlamPlate(state.occupancy) : null;

  // ── Final Sequence ────────────────────────────────────
  // 라운드당 한 번만 묻는다. 조건("상대는 0, 나는 남음")은 그 뒤로도 계속 참이라
  // 그냥 두면 남은 공을 던질 때마다 다시 물어보게 된다.
  let finalSequenceOffered: TeamId | null = null;
  const current = state.turnTeam;
  if (
    slam === null &&
    !state.finalSequenceDone &&
    state.teams[otherTeam(current)].ballsRemaining === 0 &&
    state.teams[current].ballsRemaining > 0
  ) {
    state.finalSequenceTeam = current;
    state.finalSequenceDone = true;
    finalSequenceOffered = current;
  }

  // ── 승리 조건 ─────────────────────────────────────────
  if (state.phase === "overtime" && (shooterResult.score.total > 0 || opponentResult.score.total > 0)) {
    // 서든데스: 먼저 득점한 쪽 승리. 한 샷에서 양팀 다 득점하면 이번 샷 획득 점수가 큰 쪽.
    const winner =
      opponentResult.score.total > shooterResult.score.total ? opp : team;
    finishMatch(state, winner, "overtime");
  } else if (config.targetScore !== null) {
    const t0 = state.teams[0].score;
    const t1 = state.teams[1].score;
    if (t0 >= config.targetScore || t1 >= config.targetScore) {
      finishMatch(state, t0 === t1 ? null : t0 > t1 ? 0 : 1, "score");
    }
  }

  const outOfBalls = isRoundOver(state);

  return {
    sim,
    seed,
    shooterScore: shooterResult.score,
    opponentScore: opponentResult.score,
    knockouts,
    totals: [state.teams[0].score, state.teams[1].score],
    turnEnded,
    turnEndReason,
    finalSequenceOffered,
    roundOver: slam !== null || outOfBalls,
    roundOverReason: slam !== null ? "grand_slam" : outOfBalls ? "out_of_balls" : null,
    grandSlam: slam,
  };
}

/** 턴 제한시간 초과. 공은 소모하지 않고 턴만 넘긴다. */
export function applyTimeout(state: MatchState, config: RulesConfig): { afk: boolean } {
  const team = state.teams[state.turnTeam];
  team.consecutiveTimeouts++;
  team.shotCursor++;
  state.turnTeam = otherTeam(state.turnTeam);
  return { afk: team.consecutiveTimeouts >= config.maxConsecutiveTimeouts };
}

/**
 * Final Sequence에서 Bank Points 선택 — 남은 공을 점수로 환산하고 턴을 끝낸다.
 * 규칙 §6.
 */
export function bankPoints(state: MatchState, config: RulesConfig, team: TeamId): number {
  const t = state.teams[team];
  const gained = t.ballsRemaining * config.leftoverBallPoints;
  t.score += gained;
  t.ballsRemaining = 0;
  state.finalSequenceTeam = null;
  return gained;
}

/** Final Sequence 계속 진행 선택. */
export function continueFinalSequence(state: MatchState): void {
  state.finalSequenceTeam = null;
}

/** 양 팀 공이 모두 소진되면 라운드를 더 진행할 수 없다. */
export function isRoundOver(state: MatchState): boolean {
  return state.teams[0].ballsRemaining === 0 && state.teams[1].ballsRemaining === 0;
}

/** 라운드를 끝내는 쪽(서버·로컬 세션)이 "왜 끝났는지"를 알려준다. */
export interface RoundEndCause {
  reason: RoundEndReason;
  /** reason이 "grand_slam"일 때 어느 판을 누가 채웠는지 */
  grandSlam?: { astrolabe: AstrolabeId; team: TeamId } | null;
}

export interface RoundEndResult {
  leftoverBonus: [number, number];
  totals: [number, number];
  nextPhase: "intermission" | "overtime" | "ended";
  reason: RoundEndReason;
  grandSlam: { astrolabe: AstrolabeId; team: TeamId } | null;
}

/**
 * 라운드를 끝내고 잔여 공 보너스를 반영한다 (규칙 §3 — 남은 공 1개당 1점).
 * Grouping 된 공은 이미 손을 떠났으므로 자동으로 제외된다.
 *
 * **만관으로 끊긴 라운드도 잔여 공 보너스를 받는다.** 만관을 완성한 쪽은 공을
 * 많이 썼을 테니 보너스가 적고, 끊긴 쪽은 손에 남은 공을 점수로 챙긴다 —
 * 만관 즉시 종료가 일방적인 처벌이 되지 않게 하는 균형추다.
 */
export function endRound(
  state: MatchState,
  config: RulesConfig,
  nowMs: number,
  end: RoundEndCause,
): RoundEndResult {
  const { reason } = end;
  const grandSlam = end.grandSlam ?? null;
  const leftoverBonus: [number, number] = [0, 0];
  for (const id of [0, 1] as const) {
    const bonus = state.teams[id].ballsRemaining * config.leftoverBallPoints;
    state.teams[id].score += bonus;
    state.teams[id].ballsRemaining = 0;
    leftoverBonus[id] = bonus;
  }

  const totals: [number, number] = [state.teams[0].score, state.teams[1].score];
  const base = { leftoverBonus, totals, reason, grandSlam };

  if (state.round < config.roundCount) {
    // 다음 라운드가 남았다 — 인터미션 동안 자리를 교대한다.
    state.phase = "intermission";
    return { ...base, nextPhase: "intermission" };
  }

  if (state.phase === "overtime") {
    // 연장까지 갔는데 아무도 못 넣었으면 무승부
    finishMatch(state, totals[0] === totals[1] ? null : totals[0] > totals[1] ? 0 : 1, "overtime");
    return { ...base, nextPhase: "ended" };
  }

  if (totals[0] === totals[1]) {
    state.phase = "intermission";
    state.phaseEndsAtMs = nowMs;
    return { ...base, nextPhase: "overtime" };
  }

  finishMatch(state, totals[0] > totals[1] ? 0 : 1, "score");
  return { ...base, nextPhase: "ended" };
}

export function finishMatch(
  state: MatchState,
  winner: TeamId | null,
  reason: "score" | "overtime" | "forfeit",
): void {
  state.phase = "ended";
  state.winner = winner;
  state.endReason = reason;
  state.finalSequenceTeam = null;
}

/** 현재 페이즈가 슈팅을 받을 수 있는 상태인지. */
export function acceptsShots(phase: MatchPhase): boolean {
  return phase === "round" || phase === "overtime";
}
